import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { BuildingType, iconForBuildingType } from '../../../types/BuildingTypeEnums';
import { translate } from '../../../types/EnumTranslations';
import { useEnabledBuildingTypes } from '../../../hooks/useEnabledBuildingTypes';
import { offeredBuildingTypes } from '../../../Helpers/buildingTypeOptions';

interface BuildingTypeEditModalProps {
    show: boolean;
    onHide: () => void;
    onSave: (buildingType: BuildingType) => Promise<void>;
    buildingType?: BuildingType;
    submitting: boolean;
}

export const BuildingTypeEditModal: React.FC<BuildingTypeEditModalProps> = ({
    show,
    onHide,
    onSave,
    buildingType,
    submitting,
}) => {
    const [selectedType, setSelectedType] = useState<BuildingType>(BuildingType.standard);
    const [error, setError] = useState<string | null>(null);

    // The instance's enabled types plus the building's current one
    const enabledTypes = useEnabledBuildingTypes();
    const buildingTypeOptions = useMemo(
        () => offeredBuildingTypes(enabledTypes ?? null, [buildingType ?? BuildingType.standard]),
        [enabledTypes, buildingType],
    );

    // Populate field when modal opens
    useEffect(() => {
        if (show) {
            setSelectedType(buildingType ?? BuildingType.standard);
            setError(null);
        }
    }, [show, buildingType]);

    const handleSave = async () => {
        try {
            await onSave(selectedType);
            onHide();
        } catch (err) {
            console.error('Error saving building type:', err);
            setError('Kunde inte spara. Försök igen.');
        }
    };

    const handleClose = () => {
        setError(null);
        onHide();
    };

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>Ändra byggnadstyp</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && (
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                )}

                <Form.Group>
                    <Form.Label>Byggnadstyp</Form.Label>
                    <Form.Select
                        value={selectedType}
                        onChange={(e) => setSelectedType(e.target.value as BuildingType)}
                        disabled={submitting}
                    >
                        {buildingTypeOptions.map((type) => (
                            <option key={type} value={type}>
                                {translate(type)}
                            </option>
                        ))}
                    </Form.Select>
                </Form.Group>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '16px', color: '#666' }}>
                    <img
                        src={iconForBuildingType(selectedType)}
                        style={{ width: '15px', height: '15px' }}
                        alt=""
                    />
                    {translate(selectedType)}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={submitting}>
                    Avbryt
                </Button>
                <Button variant="primary" onClick={handleSave} disabled={submitting}>
                    {submitting ? 'Sparar...' : 'Spara'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
